import React, { useState, useEffect, useMemo, useSyncExternalStore } from 'react';
import { supabase } from '../../services/supabaseClient';
import { isHourlyUnit } from '../../services/projectLv';
import { selectBillableServices } from '../../services/billingSelection';
import { useBestandteilAbschluss } from '../bestandteile/useBestandteilAbschluss';
import { BestandteilAbschlussHinweis } from '../bestandteile/BestandteilAbschlussHinweis';
import { BestandteilStundenPflicht } from '../bestandteile/BestandteilStundenPflicht';
import { bestimmeAbschlussFall, schonGebuchtHinweis, schrittOhneLeistungsbuchung, stundenFensterOffen } from '../bestandteile/abschlussLogic';
import { POSITION_FEHLT_TEXT, projektDesVorgangs, verworfenText } from '../bestandteile/abschlussSchreibweg';
import { queueJournalMirror } from '../../services/serviceJournal';
import { PlanToolbar } from './PlanToolbar';
import { ArbeitsschritteListe } from './ArbeitsschritteListe';
import { naechsteSortierung } from './abschnittOrdnung';
import { SchrittDetail } from './SchrittDetail';
import { MUSTER_ZEILEN } from './musterVorlage';
import { BlockManager } from './BlockManager';
import { StempelfeldEditor } from './StempelfeldEditor';
import { DokumentenUpload } from './DokumentenUpload';
import { PlanVorschau } from './PlanVorschau';
import { DruckNachtragenDialog } from './DruckNachtragenDialog';
import { BillingSelectionModal } from '../BillingSelectionModal';
import { usePdfExport, type ExportierterPlan } from './hooks/usePdfExport';
import { useRechte } from '../../hooks/useRechte';
import { SigeProject, Arbeitsschritt, Gefaehrdung, Schutzmassnahme, SigeBlock, KatalogArbeit, HAUPTABSCHNITTE, mitPlanSchrift, planHatZeitachse } from './types';
import { buildSigeVersionCopy, describeSigeVersion } from './versioning';
import {
  ANLAGE_ABGEBROCHEN_TEXT, gespeichertePlotBreite, mitPlotBreiteZumSpeichern, sigePlanVorlagenFuerAnlage, useSigePlanVorlagen,
  vorbelegungNeuerPlan,
} from '../../services/sigePlanVorlagen';
import { SiGePlanWizard } from './SiGePlanWizard';
import { DokumentePanel } from '../dokumente/DokumentePanel';
import { VollstaendigkeitErgebnis, pruefeSigePlanVollstaendigkeit } from './vollstaendigkeit';
import { ArrowLeft, Loader2, Copy, FileUp, History, Save, Wand2 } from 'lucide-react';
import { AltplanImportDialog } from './AltplanImportDialog';
import { Report } from '../../types';
import { formatDateLocal } from '../../services/dateService';
import { holdIdleExit } from '../../services/idleHold';
import { ablageFehler, ablageStandText, ablageStandTon, type AblageTon } from '../../services/ablageHilfen';
import {
  baueSigePlanAblageAuftrag, sigePlanAblageAnlass, sollSigePlanAblegen, wiederverwendbarerAuftrag,
  type SigePlanAblageAnlass, type SigePlanAblageAuftrag, type SigePlanAblageStand,
} from './planAblage';

/** Ton des Banners je Ablage-Ergebnis — eine Warnung (verwaiste Datei) ist kein Grün. */
const BANNER_TON: Record<AblageTon, 'erfolg' | 'warnung' | 'fehler'> = {
  laeuft: 'warnung',
  erfolg: 'erfolg',
  warnung: 'warnung',
  fehler: 'fehler',
};
import { legeSigePlanAb } from './planAblageAnschluss';
import type { PlanStandFuerAblage } from './PlanVorschau';
import { abrechnungsBuchungsText, abrechnungsSchritt, nachPlotSchliessen, plotAbrechnungsEnde, type AbrechnungsZiel } from './planAbrechnung';
import {
  hatOffenePlotAbrechnung, legePlotAbrechnungZurueck, nimmPlotAbrechnung, offenePlotAbrechnungen, plotAbrechnungenAbonnieren,
  type OffenePlotAbrechnung,
} from './plotAbrechnungen';
import { AbweichungsHinweis } from '../common/AbweichungsHinweis';

/**
 * Liest den Zeilenbaum EINER Version frisch aus der Datenbank — Grundlage für
 * die Kopie. Der lokale Zustand taugt dafür nicht zuverlässig: der
 * Detail-Editor schreibt verzögert und meldet den Stand erst nach dem Schreiben
 * nach oben, eine Kopie aus dem Zustand könnte also hinter der Datenbank
 * liegen.
 */
const fetchVersionTree = async (sigeProjektId: string) => {
  const { data: steps, error: stepsError } = await supabase
    .from('sige_arbeitsschritte').select('*').eq('sige_projekt_id', sigeProjektId).order('sortierung');
  if (stepsError) throw new Error(`Arbeitsschritte konnten nicht gelesen werden: ${stepsError.message}`);

  const stepIds = (steps || []).map((s: any) => s.id);
  let gefaehrdungen: any[] = [];
  if (stepIds.length > 0) {
    const { data, error } = await supabase
      .from('sige_gefaehrdungen').select('*').in('arbeitsschritt_id', stepIds);
    if (error) throw new Error(`Gefährdungen konnten nicht gelesen werden: ${error.message}`);
    gefaehrdungen = data || [];
  }

  let schutzmassnahmen: any[] = [];
  if (gefaehrdungen.length > 0) {
    const { data, error } = await supabase
      .from('sige_schutzmassnahmen').select('*').in('gefaehrdung_id', gefaehrdungen.map(g => g.id));
    if (error) throw new Error(`Schutzmaßnahmen konnten nicht gelesen werden: ${error.message}`);
    schutzmassnahmen = data || [];
  }

  const { data: blocks, error: blockError } = await supabase
    .from('sige_bloecke').select('*').eq('sige_projekt_id', sigeProjektId);
  if (blockError) throw new Error(`Layout-Blöcke konnten nicht gelesen werden: ${blockError.message}`);

  return {
    arbeitsschritte: (steps || []) as any[],
    gefaehrdungen,
    schutzmassnahmen,
    bloecke: (blocks || []) as any[],
  };
};

/** Vollständig gelesener Zustand einer Version — erst gelesen, dann übernommen. */
interface SigeTreeState {
  arbeitsschritte: Arbeitsschritt[];
  gefaehrdungen: Record<string, Gefaehrdung[]>;
  massnahmen: Record<string, Schutzmassnahme[]>;
  bloecke: SigeBlock[];
  hauptabschnitte: string[];
  /** null = Katalogabfrage lieferte nichts, Musterliste bleibt stehen. */
  katalog: KatalogArbeit[] | null;
}

interface SiGePlanEditorProps {
  project: { id: string, name?: string, data: Report };
  userProfile: any;
  onBack: () => void;
  onUpdate?: (report: Report) => void;
  /**
   * Schreibgeschuetzt geoeffnetes Projekt. Dieses Modul schreibt an vielen
   * Stellen DIREKT nach Supabase (sige_projekte, sige_arbeitsschritte,
   * sige_bloecke, sige_gefaehrdungen, sige_pikto_favoriten …) — nicht nur hier,
   * sondern auch in den Unterkomponenten. Es gibt also keinen einzelnen
   * Save-Pfad, den man verriegeln koennte; im Lesemodus bleibt der Editor
   * deshalb ganz zu.
   */
  isReadOnly?: boolean;
}

export const SiGePlanEditor: React.FC<SiGePlanEditorProps> = ({ project, userProfile, onBack, onUpdate, isReadOnly = false }) => {
  const [loading, setLoading] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [sigeProject, setSigeProject] = useState<SigeProject | null>(null);
  // Jede Version ist eine eigene Zeile in sige_projekte mit derselben project_id.
  const [versions, setVersions] = useState<SigeProject[]>([]);
  const [arbeitsschritte, setArbeitsschritte] = useState<Arbeitsschritt[]>([]);
  const [gefaehrdungen, setGefaehrdungen] = useState<Record<string, Gefaehrdung[]>>({});
  const [massnahmen, setMassnahmen] = useState<Record<string, Schutzmassnahme[]>>({});
  const [bloecke, setBloecke] = useState<SigeBlock[]>([]);
  const [hauptabschnitte, setHauptabschnitte] = useState<string[]>([...HAUPTABSCHNITTE]);
  const [katalog, setKatalog] = useState<KatalogArbeit[]>([...MUSTER_ZEILEN]);

  const [selectedSchrittId, setSelectedSchrittId] = useState<string | null>(null);

  // Modals
  const [isStempelfeldOpen, setIsStempelfeldOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isVorschauOpen, setIsVorschauOpen] = useState(false);
  // „Druck nachtragen“: direkt vom Rechner geplotteter Plan, zählt gegen die Papierrolle.
  const [druckNachtragenOffen, setDruckNachtragenOffen] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  // Preise im Abrechnungsdialog nur mit dem Recht „Preise sehen" (Admins immer).
  const rechte = useRechte(userProfile?.role);
  /**
   * Abschlussschritt nach Versand bzw. Freigabe (Plan Paketposition B5). Jede
   * Ausgabe des Plans (Export mit Buchung = Versand, Ablage einer geprüften
   * Fassung aus der Vorschau = Freigabe) erledigt per Trigger den Bestandteil
   * „SiGe-Plan" einer Paketposition; der Hook meldet das und bucht ein
   * dadurch vollständiges Paket. Vor den frühen Rückgaben (Hook-Reihenfolge).
   */
  const abschluss = useBestandteilAbschluss({
    report: project.data, onUpdate, userProfile, quelle: 'sigeplan', isReadOnly, projectId: project.id,
  });
  /**
   * Fall 2 (nur ein Paket, keine eigene SiGe-Plan-Position): statt der
   * Positionsauswahl die Frage, ob der Export als Fassung hinausgeht — nur
   * dann entsteht eine Ausgabe, die den Bestandteil erledigt. Ein blosser
   * Download bleibt ein Zwischenstand wie bisher.
   */
  const [paketExportFrage, setPaketExportFrage] = useState(false);
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  // „Altplan einlesen“: SiGe-Plan aus der Zeit vor der App als neue Version.
  const [altplanOffen, setAltplanOffen] = useState(false);
  const [versionLabel, setVersionLabel] = useState('');
  const [isDuplicating, setIsDuplicating] = useState(false);
  // Kein alert()/confirm(): die App läuft ggf. im Sandbox-iframe, der native
  // Dialoge blockt — Meldungen laufen über ein Inline-Banner, Rückfragen über
  // einen eigenen Bestätigungszustand (Muster wie in ArbeitsschritteListe).
  const [hinweis, setHinweis] = useState<{
    text: string;
    ton: 'fehler' | 'warnung' | 'erfolg';
    /** Ein Knopf im Banner — z. B. „Erneut ablegen", wenn die Ablage scheiterte. */
    aktion?: { label: string; onClick: () => void };
  } | null>(null);
  // Der letzte Versuch, den Plan in der zentralen Ablage abzulegen (Stufe 1A):
  // aus der Vorschau per Knopf oder automatisch beim Export mit Buchung. Die
  // Vorschau zeigt ihn, solange sie denselben Stand zeigt.
  const [ablage, setAblage] = useState<SigePlanAblageStand | null>(null);
  // Anmerkungen des Ablage-Kerns zur letzten Ablage (Journal-Eintrag, Steckbrief,
  // Stufe 6.1) — kein Fehler, deshalb eine eigene dezente Zeile statt des Banners.
  const [ablageHinweise, setAblageHinweise] = useState<string[]>([]);
  const [exportOhneAbrechnungConfirm, setExportOhneAbrechnungConfirm] = useState(false);
  // Wofür die Abrechnung läuft: Download oder ein schon gesendeter Plot
  // (planAbrechnung.ts). Gesetzt, bevor ein Abrechnungsdialog aufgeht.
  const [abrechnungsZiel, setAbrechnungsZiel] = useState<AbrechnungsZiel>({ art: 'download' });
  // Angekommene Plot-Aufträge aller Projekte, die auf ihre Abrechnung warten
  // (plotAbrechnungen.ts) — abgeholt werden nur die dieses Projekts.
  const offenePlots = useSyncExternalStore(plotAbrechnungenAbonnieren, offenePlotAbrechnungen, offenePlotAbrechnungen);
  /**
   * Die abgeholte Plot-Abrechnung, solange ihr Abrechnungsschritt offen ist.
   * Beim Abholen verlässt sie die Warteschlange; wird der Editor mit offener
   * Paket-Frage oder Positionsauswahl verlassen (Projektwechsel, zurück zum
   * Projekt), geht sie dorthin zurück (Codex 23.10.2026) — sonst wäre der
   * geplottete Plan nie gebucht und nie als Fassung abgelegt.
   */
  const plotInAbrechnungRef = React.useRef<OffenePlotAbrechnung<'sigeplan'> | null>(null);
  useEffect(() => () => {
    const eintrag = plotInAbrechnungRef.current;
    plotInAbrechnungRef.current = null;
    if (eintrag) legePlotAbrechnungZurueck(eintrag);
  }, []);
  /**
   * Ein Plot wurde abgerechnet und wird abgelegt: danach schließt der Editor
   * (planAbrechnung.ts, `nachPlotSchliessen`). Hält das Projekt fest, für das
   * das gilt — ein Projektwechsel dazwischen schließt nichts.
   */
  const [schliessenNachPlot, setSchliessenNachPlot] = useState<string | null>(null);
  // Ein Download-Export läuft (bis zu seiner Ablage): solange schließt der
  // Editor nicht, und ein angekommener Plot wartet mit seiner Abrechnung.
  const [exportLaeuft, setExportLaeuft] = useState(false);
  // Export-Gate: die harte Prüfung läuft vor jedem Download, blockiert aber
  // nicht — sie legt nur offen, was im Plan fehlt.
  const [exportPruefung, setExportPruefung] = useState<VollstaendigkeitErgebnis | null>(null);

  // Anlage-Assistent
  const [wizardOffen, setWizardOffen] = useState(false);
  // Die Projektablage, eingebettet in den Plan. Bewusst KEIN Plan-Guard
  // (`mitPlan`): Plaene und Beurteilungen liest man auch, bevor der SiGe-Plan
  // selbst angelegt ist — oft genau deswegen.
  const [dokumenteOffen, setDokumenteOffen] = useState(false);
  // Der Autostart darf je geladenem Projekt nur einmal greifen — sonst ginge
  // der Assistent nach jedem Schließen sofort wieder auf.
  const autostartGeprueft = React.useRef(false);

  const { exportPdf } = usePdfExport();
  // Plotter-Vorlagen der Firma: Schriftvorgabe für den Regler der Vorschau.
  // Wo eine Breite dauerhaft gespeichert wird (neuer Plan, Versionskopie eines
  // Plans ohne Breite), kommen die Vorgaben aus sigePlanVorlagenFuerAnlage.
  const sigePlanVorlagen = useSigePlanVorlagen();

  // Der Detail-Editor speichert verzögert (1,2 s). Vor jedem Versionswechsel
  // und vor jeder Kopie muss ein ausstehender Schreibvorgang zuerst in der
  // Datenbank landen, sonst fehlt die Änderung im Ziel.
  const schrittFlushRef = React.useRef<(() => Promise<void>) | null>(null);
  // Laufsperren der Anlagewege (Versionskopie, Projekt-Eintrag erzwingen).
  const versionAnlageLaeuftRef = React.useRef(false);
  const erzwingenLaeuftRef = React.useRef(false);
  const registerSchrittFlush = React.useCallback((flush: (() => Promise<void>) | null) => {
    schrittFlushRef.current = flush;
  }, []);
  const flushSchrittEdits = async () => {
    await schrittFlushRef.current?.();
  };

  const meta = project.data.metadata;
  const beteiligte = meta?.distributionList || [];

  // Fusszeile des Plans: Projekt-Nr. und -bezeichnung kommen aus dem
  // Hauptprojekt, der SiGe-Plan selbst kennt nur seine Plan-Nr. Memoisiert,
  // damit die Vorschau das PDF nicht bei jedem Render des Editors neu baut.
  const fusszeile = useMemo(
    () => ({ projektNummer: meta?.projectNumber, projektName: project.name || meta?.projectName }),
    [meta?.projectNumber, meta?.projectName, project.name],
  );

  // LV-Positionen für SiGe-Plan (Abrechnung beim PDF-Export).
  // Zuordnung zuerst, Titel nur als Rückfallebene, Positionsnummern nie —
  // die Nummerierung gehört dem Kunden (siehe selectBillableServices).
  // `fallbackToAllServices` erhält das bisherige Verhalten dieses Editors:
  // trifft gar nichts, stehen sämtliche Leistungen zur Wahl.
  const billingServices = useMemo(
    () => selectBillableServices(meta?.globalData?.services || [], 'sigeplan', { fallbackToAllServices: true }),
    [meta],
  );
  // Die EIGENEN SiGe-Plan-Positionen — ohne Rückfallebene. Nur sie
  // entscheiden über den Fall (Plan B5) und über den Satz „bereits gebucht":
  // eine fremde Position aus „alle Leistungen" ist keine Pauschale dieses Plans.
  const eigenePositionen = useMemo(
    () => selectBillableServices(meta?.globalData?.services || [], 'sigeplan'),
    [meta],
  );

  useEffect(() => {
    autostartGeprueft.current = false;
    loadData();
  }, [project.id]);

  /**
   * Autostart des Assistenten: ohne Plan-Datensatz oder ohne einen einzigen
   * Arbeitsschritt ist der Editor für Neulinge eine leere Fläche. Greift genau
   * einmal je geladenem Projekt.
   */
  useEffect(() => {
    if (loading || initError || autostartGeprueft.current) return;
    autostartGeprueft.current = true;
    if (!sigeProject || arbeitsschritte.length === 0) setWizardOffen(true);
  }, [loading, initError, sigeProject, arbeitsschritte.length]);

  /**
   * Liest den vollständigen Zustand EINER Version, ohne React-Zustand
   * anzufassen. Erst wenn alle Abfragen erfolgreich waren, darf
   * `applyTreeState` das Ergebnis übernehmen — schlägt eine Abfrage fehl,
   * bleibt die bisher geladene Version unverändert nutzbar (früher wurde der
   * Zustand vorab geleert, ein Fehler hinterließ dann einen leeren Editor).
   */
  const fetchTreeState = async (sp: SigeProject | null): Promise<SigeTreeState> => {
    const raw = sp
      ? await fetchVersionTree(sp.id)
      : { arbeitsschritte: [] as any[], gefaehrdungen: [] as any[], schutzmassnahmen: [] as any[], bloecke: [] as any[] };

    const fetchedSteps = raw.arbeitsschritte as Arbeitsschritt[];

    const gefMap: Record<string, Gefaehrdung[]> = {};
    raw.gefaehrdungen.forEach(g => {
      if (!gefMap[g.arbeitsschritt_id]) gefMap[g.arbeitsschritt_id] = [];
      gefMap[g.arbeitsschritt_id].push(g);
    });

    const masMap: Record<string, Schutzmassnahme[]> = {};
    raw.schutzmassnahmen.forEach(m => {
      if (!masMap[m.gefaehrdung_id]) masMap[m.gefaehrdung_id] = [];
      masMap[m.gefaehrdung_id].push(m);
    });

    // Hauptabschnitte laden:
    // Wir starten IMMER mit den 10 Standard-Abschnitten (HAUPTABSCHNITTE) als absolute, unverlierbare Basis!
    const finalAbschnitte = [...HAUPTABSCHNITTE] as string[];

    // 1. Projektspezifische Abschnitte aus sige_projekte hinzumergen
    if (sp && Array.isArray((sp as any).hauptabschnitte)) {
      (sp as any).hauptabschnitte.forEach((h: string) => {
        if (h && !finalAbschnitte.includes(h)) {
          finalAbschnitte.push(h);
        }
      });
    }

    // 2. Globale Abschnitte aus der extra Tabelle hinzumergen
    try {
      const { data: haData, error: haError } = await supabase
        .from('sige_hauptabschnitte')
        .select('name, sortierung')
        .order('sortierung');
      if (haError) throw haError;
      if (haData?.length) {
        haData.forEach(h => {
          if (h.name && !finalAbschnitte.includes(h.name)) {
            finalAbschnitte.push(h.name);
          }
        });
      }
    } catch (e) {
      console.error('Error loading global sige_hauptabschnitte', e);
    }

    // 3. Fallback/Ensure: Auch wenn Abschnitte nicht in der DB Spalte / globalen Tabelle waren,
    // scannen wir alle bereits verwendeten Abschnitte der Arbeitsschritte dieses Projekts,
    // damit diese unter keinen Umständen verloren gehen!
    const usedAbschnitte = fetchedSteps
      .map(a => a.hauptabschnitt)
      .filter((h): h is string => !!h);

    usedAbschnitte.forEach(ua => {
      if (!finalAbschnitte.includes(ua)) {
        finalAbschnitte.push(ua);
      }
    });

    // Der Katalog ist optional: schlägt die Abfrage fehl oder ist sie leer,
    // bleibt die Musterliste stehen (null = nicht übernehmen).
    let katalogEntries: KatalogArbeit[] | null = null;
    const { data: katData, error: katError } = await supabase
      .from('sige_arbeiten_katalog').select('*').order('bezeichnung');
    if (!katError && katData?.length) {
      const musterBez = new Set(MUSTER_ZEILEN.map(m => m.bezeichnung));
      katalogEntries = [
        ...MUSTER_ZEILEN,
        ...(katData as KatalogArbeit[]).filter(k => !musterBez.has(k.bezeichnung)),
      ];
    }

    return {
      arbeitsschritte: fetchedSteps,
      gefaehrdungen: gefMap,
      massnahmen: masMap,
      bloecke: (raw.bloecke || []) as SigeBlock[],
      hauptabschnitte: finalAbschnitte,
      katalog: katalogEntries,
    };
  };

  /**
   * Übernimmt einen vollständig gelesenen Zustand in einem Rutsch. Nur hier
   * werden Reste der vorigen Version verworfen.
   */
  const applyTreeState = (state: SigeTreeState) => {
    setArbeitsschritte(state.arbeitsschritte);
    setGefaehrdungen(state.gefaehrdungen);
    setMassnahmen(state.massnahmen);
    setBloecke(state.bloecke);
    setSelectedSchrittId(state.arbeitsschritte[0]?.id ?? null);
    setHauptabschnitte(state.hauptabschnitte);
    if (state.katalog) setKatalog(state.katalog);
  };

  /**
   * Lädt die Versionsliste und anschließend eine davon.
   * `preferVersionId` gewinnt, sonst bleibt die aktuell gewählte Version
   * ausgewählt, sonst die neueste.
   */
  const loadData = async (preferVersionId?: string) => {
    setLoading(true);
    setInitError(null);
    try {
      // Fetch SigeProject - using select list ordered by created_at desc to avoid multiple row PGRST116 errors
      let { data: spList, error: fetchError } = await supabase
        .from('sige_projekte')
        .select('*')
        .eq('project_id', project.id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (fetchError) {
        throw new Error(`Fehler beim Abrufen des SiGe-Projekts: ${fetchError.message} (Code: ${fetchError.code})`);
      }

      const list = (spList || []) as SigeProject[];
      const wantedId = preferVersionId || sigeProject?.id;
      // Kein stiller Insert mehr: existiert noch kein Plan, übernimmt der
      // Anlage-Assistent (Autostart weiter oben) die Neuanlage in Schritt 1.
      // Vorher entstand hier ein halbleerer Datensatz, den nie jemand bestellt
      // hatte — inklusive Fallback-Zeile ohne Bauherr und ohne Bauzeit.
      const sp: SigeProject | null =
        (wantedId ? list.find(v => v.id === wantedId) : undefined) || list[0] || null;

      const nextList = sp && !list.some(v => v.id === sp.id) ? [sp, ...list] : list;

      // Erst lesen, dann übernehmen: schlägt das Lesen fehl, bleibt die zuvor
      // geladene Version stehen statt einen leeren Editor zu hinterlassen.
      const tree = await fetchTreeState(sp);
      setVersions(nextList);
      setSigeProject(sp);
      applyTreeState(tree);
    } catch (e: any) {
      console.error('Error loading SiGe Plan data', e);
      setInitError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  const handleAbschnittCreated = async (name: string) => {
    // Zweite Reihe hinter der Zugangssperre: diese Funktionen schreiben direkt
    // nach Supabase und duerfen im Lesemodus unter keinen Umstaenden laufen.
    if (isReadOnly) return;
    if (!name.trim()) return;
    const cleanName = name.trim();
    const updated = hauptabschnitte.includes(cleanName) ? hauptabschnitte : [...hauptabschnitte, cleanName];
    setHauptabschnitte(updated);
    if (sigeProject) {
      setSigeProject(curr => curr ? { ...curr, hauptabschnitte: updated } : null);
      const { error } = await supabase
        .from('sige_projekte')
        .update({ hauptabschnitte: updated })
        .eq('id', sigeProject.id);
      if (error) {
        console.warn('Persisting custom sections to sige_projekte failed:', error.message);
      }
    }
  };

  // Nach „Abschnitt zusammenführen“ steht der Quellabschnitt leer: er fällt
  // aus der eigenen Liste des Plans. Standardabschnitte bleiben, die globale
  // Tabelle sige_hauptabschnitte bleibt unberührt (gilt für alle Projekte).
  const handleAbschnittLeer = async (name: string) => {
    if (isReadOnly) return;
    const cleanName = name.trim();
    if (!cleanName || (HAUPTABSCHNITTE as readonly string[]).includes(cleanName)) return;
    setHauptabschnitte(prev => prev.filter(h => h !== cleanName));
    const eigene = sigeProject?.hauptabschnitte || [];
    if (!sigeProject || !eigene.includes(cleanName)) return;
    const updated = eigene.filter(h => h !== cleanName);
    setSigeProject(curr => curr ? { ...curr, hauptabschnitte: updated } : null);
    const { error } = await supabase
      .from('sige_projekte')
      .update({ hauptabschnitte: updated })
      .eq('id', sigeProject.id);
    if (error) {
      console.warn('Removing an emptied section from sige_projekte failed:', error.message);
    }
  };

  // Nach „Arbeitsschritte zusammenführen“: die Gefährdungen der gelöschten
  // Zeilen hängen in der Datenbank schon an der Zielzeile — lokal nachziehen,
  // sonst fehlen ihre Maßnahmen bis zum Neuladen in Vorschau und Druck.
  const handleGefaehrdungenUmgehaengt = (vonIds: string[], nachId: string) => {
    setGefaehrdungen(prev => {
      const umgehaengt = vonIds.flatMap(id => (prev[id] || []).map(g => ({ ...g, arbeitsschritt_id: nachId })));
      if (!umgehaengt.length) return prev;
      const next = { ...prev };
      vonIds.forEach(id => { delete next[id]; });
      next[nachId] = [...(prev[nachId] || []), ...umgehaengt];
      return next;
    });
  };

  const updateSigeProject = async (updates: Partial<SigeProject>) => {
    if (isReadOnly) return;
    if (!sigeProject) return;
    const vorher = sigeProject;
    const merged = { ...sigeProject, ...updates };
    setSigeProject(merged);
    setVersions(prev => prev.map(v => (v.id === merged.id ? merged : v)));
    const { error } = await supabase.from('sige_projekte').update(updates).eq('id', sigeProject.id);
    if (error) {
      // Der Stand war optimistisch gesetzt. Ohne Ruecknahme druckte der
      // Download etwas, das nach dem Neuladen nicht mehr da ist — still. Nur
      // zuruecksetzen, wenn inzwischen nichts Neueres gesetzt wurde.
      setSigeProject(aktuell => (aktuell === merged ? vorher : aktuell));
      setVersions(prev => prev.map(v => (v === merged ? vorher : v)));
      setHinweis({ ton: 'fehler', text: `Plan-Einstellungen nicht gespeichert: ${error.message}` });
      throw new Error(error.message);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // VERSIONEN — je Version ein eigener Zeilenbaum unter sige_projekte
  // ═══════════════════════════════════════════════════════════

  const handleSelectVersion = async (id: string) => {
    if (!id || id === sigeProject?.id) return;
    const target = versions.find(v => v.id === id);
    if (!target) return;
    setHinweis(null);
    // Ausstehende Änderung der bisherigen Version zuerst sichern.
    try {
      await flushSchrittEdits();
    } catch (e: any) {
      console.error('Ausstehende Änderung am Arbeitsschritt konnte nicht gesichert werden', e);
      setHinweis({
        ton: 'fehler',
        text: `Eine noch nicht gesicherte Änderung am Arbeitsschritt konnte nicht gespeichert werden; der Versionswechsel wurde abgebrochen. (${e?.message || String(e)})`,
      });
      // Neue Referenz erzwingt ein Rendern; ohne das behielte das Auswahlfeld
      // die abgebrochene Auswahl, obwohl die alte Version geladen bleibt.
      setVersions(prev => [...prev]);
      return;
    }
    setLoading(true);
    // Zielversion erst vollständig lesen, dann in einem Rutsch übernehmen.
    // Andernfalls stünde der Editor nach einem Lesefehler leer da und ein
    // erneuter Klick auf dieselbe Version würde von der Gleichheitsprüfung
    // oben verworfen.
    try {
      const tree = await fetchTreeState(target);
      setSigeProject(target);
      applyTreeState(tree);
      setInitError(null);
    } catch (e: any) {
      console.error('Error loading SiGe Plan version', e);
      setHinweis({
        ton: 'fehler',
        text: `Die Version konnte nicht geladen werden; es bleibt die bisherige Version geladen. (${e?.message || String(e)})`,
      });
      // Neue Referenz erzwingt ein Rendern; ohne das behielte das Auswahlfeld
      // die fehlgeschlagene Auswahl, obwohl die alte Version geladen bleibt.
      setVersions(prev => [...prev]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Dupliziert die gewählte Version samt aller Kinder in einen neuen Zeilenbaum.
   * IDs werden im Client vergeben (siehe versioning.ts), Einfügereihenfolge
   * folgt dem Fremdschlüssel-Graph. Schlägt ein Schritt fehl, werden die
   * bereits eingefügten Zeilen wieder entfernt — kein halber Baum.
   */
  const handleCreateVersion = async () => {
    if (isReadOnly) return;
    if (!sigeProject) return;
    // Laufsperre: zwei schnelle Auslöser (Enter + Klick) vor dem nächsten
    // Rendern legten sonst zwei Versionen an — der Vorgang wartet ggf. auf
    // die Plotter-Vorlagen und dauert dadurch länger.
    if (versionAnlageLaeuftRef.current) return;
    versionAnlageLaeuftRef.current = true;
    const label = versionLabel.trim() || `Version ${versions.length + 1}`;
    setShowVersionDialog(false);
    setVersionLabel('');
    setIsDuplicating(true);
    setHinweis(null);

    const insertedTables: { table: string; ids: string[] }[] = [];
    let labelPersisted = true;

    try {
      // 0. Ausstehenden Auto-Speicher-Vorgang des Detail-Editors erzwingen und
      // die Vorlage danach frisch lesen. Beides ist nötig, damit eine gerade
      // getippte Änderung in der Kopie steht und nicht nur in der Vorlage.
      await flushSchrittEdits();
      const source = await fetchVersionTree(sigeProject.id);
      // Die Kopie behält die Breite der Vorlage. Hat die Vorlage keine
      // (Altbestand NULL), bekommt die Kopie die Standardbreite der Firma
      // statt NULL — ob die Spalte NULL zulässt, ist nicht belegt. Weil die
      // Breite dann dauerhaft gespeichert wird, nach dem Laden der Vorlagen
      // (sigePlanVorlagenFuerAnlage), nicht aus dem Store im Ladeschwebezustand.
      let anlageVorlagen = sigePlanVorlagen;
      if (gespeichertePlotBreite(sigeProject.plot_breite_mm) === null) {
        const anlage = await sigePlanVorlagenFuerAnlage();
        // Sitzung gewechselt, während gewartet wurde: nichts anlegen.
        if (anlage.abgebrochen === true) throw new Error(ANLAGE_ABGEBROCHEN_TEXT);
        anlageVorlagen = anlage.vorlagen;
      }

      const copy = buildSigeVersionCopy(
        {
          project: mitPlotBreiteZumSpeichern(sigeProject, anlageVorlagen) as any,
          arbeitsschritte: source.arbeitsschritte,
          gefaehrdungen: source.gefaehrdungen,
          schutzmassnahmen: source.schutzmassnahmen,
          bloecke: source.bloecke,
        },
        label,
        () => crypto.randomUUID(),
      );

      // 1. Kopf-Zeile. Fehlt die Spalte version_label (Migration sige_versions.sql
      // noch nicht gefahren), wird die Version ohne Bezeichnung angelegt statt
      // den Vorgang abzubrechen.
      let { error: projectError } = await supabase.from('sige_projekte').insert(copy.project);
      if (projectError && /version_label/i.test(projectError.message || '')) {
        const { version_label: _version_label, ...withoutLabel } = copy.project;
        labelPersisted = false;
        ({ error: projectError } = await supabase.from('sige_projekte').insert(withoutLabel));
      }
      if (projectError) {
        throw new Error(`Neue Version konnte nicht angelegt werden: ${projectError.message}`);
      }
      insertedTables.push({ table: 'sige_projekte', ids: [copy.project.id] });

      // 2. Kinder in Fremdschlüssel-Reihenfolge
      const childInserts: { table: string; rows: any[] }[] = [
        { table: 'sige_arbeitsschritte', rows: copy.arbeitsschritte },
        { table: 'sige_gefaehrdungen', rows: copy.gefaehrdungen },
        { table: 'sige_schutzmassnahmen', rows: copy.schutzmassnahmen },
        { table: 'sige_bloecke', rows: copy.bloecke },
      ];

      for (const { table, rows } of childInserts) {
        if (!rows.length) continue;
        const { error } = await supabase.from(table).insert(rows);
        if (error) {
          throw new Error(`Kopieren von ${table} fehlgeschlagen: ${error.message}`);
        }
        insertedTables.push({ table, ids: rows.map(r => r.id) });
      }

      await loadData(copy.project.id);

      if (!labelPersisted) {
        setHinweis({
          ton: 'warnung',
          text: 'Die Version wurde angelegt, die Bezeichnung konnte jedoch nicht gespeichert werden. Bitte die Migration „sige_versions.sql" ausführen.',
        });
      }
    } catch (e: any) {
      console.error('Error duplicating SiGe Plan version', e);
      // Rücknahme in umgekehrter Reihenfolge, damit keine Reste zurückbleiben.
      for (const { table, ids } of [...insertedTables].reverse()) {
        const { error } = await supabase.from(table).delete().in('id', ids);
        if (error) console.warn(`Rücknahme in ${table} fehlgeschlagen:`, error.message);
      }
      setHinweis({ ton: 'fehler', text: `Neue Version fehlgeschlagen: ${e.message || String(e)}` });
    } finally {
      setIsDuplicating(false);
      versionAnlageLaeuftRef.current = false;
    }
  };

  // PDF-Download mit Abrechnungs-Verknüpfung:
  // Vorschau-Download → Billing-Modal → Export + Leistung verbuchen
  //
  // Plan B5: der Fall hängt an den EIGENEN SiGe-Plan-Positionen und am Paket.
  //  - Fall 1/3: die Positionsauswahl wie bisher. In Fall 3 führt „Vorgang
  //    ohne Leistungsbuchung" zur Frage unten (nachPositionsauswahl).
  //  - Fall 2 (nur ein Paket): keine Positionsauswahl — die Frage, ob der
  //    Export als Fassung hinausgeht (paketExportFrage).
  //  - weder noch: wie bisher die Auswahl aus allen Leistungen (Rückfallebene
  //    dieses Editors), sonst die Rückfrage „ohne Abrechnung".
  //  - Nur-Ansicht kommt hier nicht an (der Editor ist dann gesperrt).
  //
  // Derselbe Weg nach dem Plotten (offene Plot-Abrechnungen, unten), nur ohne
  // Download: das Blatt ist schon gedruckt.
  const starteAbrechnung = (ziel: AbrechnungsZiel) => {
    setAbrechnungsZiel(ziel);
    const schritt = abrechnungsSchritt({
      ziel: ziel.art,
      fall: bestimmeAbschlussFall({ positionen: eigenePositionen, pakete: abschluss.pakete }),
      fassungFragen: schrittOhneLeistungsbuchung({ modul: 'sigeplan', pakete: abschluss.pakete, sperre: abschluss.sperre }) === 'fassung_fragen',
      positionenAngeboten: billingServices.length > 0,
      kannBuchen: !!onUpdate,
    });
    if (schritt === 'fassung_fragen') setPaketExportFrage(true);
    else if (schritt === 'positionsauswahl') setShowBillingModal(true);
    // Inline-Rückfrage statt confirm() — siehe Kommentar am Zustand oben.
    else if (schritt === 'ohne_abrechnung') setExportOhneAbrechnungConfirm(true);
    // 'nichts' gibt es nur beim Plot: kein Schritt geht auf, nichts ist
    // abzurechnen — die abgeholte Abrechnung ist damit erledigt. Direkt an
    // der Ref, nicht über plotAbrechnungLoslassen: dessen `abrechnungsZiel`
    // stammt aus dem Render VOR diesem setAbrechnungsZiel.
    else if (ziel.art === 'plot') plotInAbrechnungRef.current = null;
  };
  const starteExportFlow = () => starteAbrechnung({ art: 'download' });

  /**
   * Angekommene Plot-Aufträge dieses Projekts abrechnen wie einen Download —
   * aber erst, wenn kein anderer Abrechnungsschritt offen ist. Die Meldung
   * kommt, wann der Server antwortet, auch mitten in einer Download-Abrechnung;
   * sie setzte sonst deren Ziel auf „plot“, und die Bestätigung buchte den
   * Plot statt herunterzuladen (Codex 22.10.2026). Jeder Plot bekommt seine
   * eigene Abrechnung, nacheinander. War der Editor beim Ankommen schon
   * verlassen, wartet der Auftrag bis zum nächsten Öffnen dieses Projekts.
   *
   * Auch die offene Vollständigkeitsprüfung (`exportPruefung`) wartet ab: ihr
   * „Trotzdem exportieren“ startet die Download-Abrechnung und setzte sonst
   * das Ziel eines eben aufgegangenen Plot-Schritts auf „download“ — die
   * Paket-Frage liegt UNTER der Prüfung (gleiche Ebene, früher im Baum), der
   * Plot verlöre sein Ziel.
   */
  const abrechnungOffen = showBillingModal || paketExportFrage || exportOhneAbrechnungConfirm;
  const ablageLaeuft = ablage?.stand.art === 'laeuft';
  const abschlussLaeuft = !!abschluss.stand?.laeuft;
  const starteAbrechnungRef = React.useRef(starteAbrechnung);
  useEffect(() => { starteAbrechnungRef.current = starteAbrechnung; });
  useEffect(() => {
    // Nur-Ansicht, Laden, Ladefehler, kein Plan: liegen lassen, nicht
    // verbrauchen — ohne `sigeProject` bucht die Bestätigung nichts.
    // Eine laufende Ablage oder ein laufender Abschlussschritt zuerst zu Ende
    // (wie in der Vorankündigung) — sonst liefen zwei Ablagen nebeneinander.
    // Eine schon abgeholte, noch nicht abgerechnete gilt — nie überschreiben.
    if (abrechnungOffen || exportPruefung || exportLaeuft || ablageLaeuft || abschlussLaeuft || isReadOnly || loading || initError
      || !sigeProject || plotInAbrechnungRef.current
      || !hatOffenePlotAbrechnung(offenePlots, project.id, 'sigeplan')) return;
    const naechster = nimmPlotAbrechnung(project.id, 'sigeplan');
    if (!naechster) return;
    // Festhalten, bis ihr Schritt endet (plotAbrechnungLoslassen) — oder
    // der Editor verlassen wird: dann legt der Unmount-Effekt sie zurück.
    plotInAbrechnungRef.current = naechster;
    starteAbrechnungRef.current({ art: 'plot', stand: naechster.stand });
  }, [abrechnungOffen, exportPruefung, exportLaeuft, ablageLaeuft, abschlussLaeuft, isReadOnly, loading, initError, sigeProject, offenePlots, project.id]);

  // Nach dem abgerechneten Plot: schließen, sobald Ablage und Abschlussschritt
  // durch sind und nichts zu lesen ist (`nachPlotSchliessen`).
  useEffect(() => {
    if (schliessenNachPlot === null) return;
    if (schliessenNachPlot !== project.id) { setSchliessenNachPlot(null); return; }
    const entscheid = nachPlotSchliessen({
      ablageLaeuft,
      ablageGescheitert: ablage?.stand.art === 'fehler',
      hinweisTon: hinweis?.ton ?? null,
      abschlussLaeuft,
      abschlussHinweise: (abschluss.stand?.zusatz.length ?? 0) + (abschluss.stand?.folge?.hinweise.length ?? 0),
      stundenOffen: stundenFensterOffen(abschluss.stand?.stunden),
      weitererSchritt: abrechnungOffen || !!exportPruefung || exportLaeuft || hatOffenePlotAbrechnung(offenePlots, project.id, 'sigeplan'),
    });
    if (entscheid === 'warten') return;
    setSchliessenNachPlot(null);
    if (entscheid === 'schliessen') onBack();
  }, [schliessenNachPlot, project.id, ablageLaeuft, ablage, hinweis, abschlussLaeuft, abschluss.stand, abrechnungOffen, exportPruefung, exportLaeuft, offenePlots, onBack]);

  /**
   * Der Plot-Schritt endet — bestätigt oder bewusst abgelehnt: die
   * festgehaltene Abrechnung loslassen (planAbrechnung.ts,
   * `plotAbrechnungsEnde`). Losgelassen wird VOR jeder Buchung und Ablage:
   * scheitert danach etwas, geht sie nicht zurück, sonst holte der Editor sie
   * sofort wieder ab und liefe in denselben Fehler. Zeigt der Editor
   * inzwischen ein anderes Projekt, geht sie zurück in die Warteschlange —
   * `false`: dann nichts buchen, nichts ablegen. Ein Download-Schritt lässt
   * sie unberührt (`true`).
   */
  const plotAbrechnungLoslassen = (): boolean => {
    const eintrag = plotInAbrechnungRef.current;
    const ende = plotAbrechnungsEnde({
      ziel: abrechnungsZiel.art, abgeholtFuer: eintrag?.projektId ?? null, offenesProjekt: project.id,
    });
    if (ende === 'unberuehrt') return true;
    plotInAbrechnungRef.current = null;
    if (ende === 'zuruecklegen' && eintrag) {
      legePlotAbrechnungZurueck(eintrag);
      return false;
    }
    return true;
  };

  /**
   * Werkzeuge der Kopfzeile brauchen einen Plan-Datensatz: ihre Dialoge rendern
   * nur mit `sigeProject`. Ohne diesen Riegel wäre der Klick auf Plankopf,
   * KI-Analyse oder Vorschau eine stille Nichtreaktion, solange der Plan noch
   * nicht über den Assistenten angelegt wurde.
   */
  const mitPlan = (oeffne: () => void) => () => {
    if (!sigeProject) {
      setHinweis({
        ton: 'warnung',
        text: 'Für dieses Projekt existiert noch kein SiGe-Plan. Bitte zuerst den Assistenten durchlaufen.',
      });
      return;
    }
    oeffne();
  };

  /**
   * Export-Gate: vor dem Download prüfen, ob der Plan überhaupt vorzeigbar ist.
   * Harte Lücken werden benannt, der Export aber nicht verweigert — es gibt
   * legitime Gründe für einen Zwischenstand.
   */
  const handleDownloadRequest = () => {
    // Ein neuer Download hebt eine vorgemerkte Schließung nach dem Plot auf —
    // sie schlösse den Editor sonst mitten in dessen Prüfung, Abrechnung oder
    // Export (Codex 02.11.2026).
    setSchliessenNachPlot(null);
    const pruefung = pruefeSigePlanVollstaendigkeit({ plan: sigeProject, arbeitsschritte, bloecke });
    if (pruefung.blockierend.length > 0) {
      setExportPruefung(pruefung);
      return;
    }
    starteExportFlow();
  };

  /**
   * Schickt einen fertigen Ablageauftrag an die zentrale Ablage (Stufe 1A, E17).
   *
   * Läuft NEBEN Download und Buchung, nie davor: scheitert die Ablage, bleibt
   * beides gültig — die Ablage ist eine Zweitschrift. Der Auftrag wird
   * FESTGEHALTEN: „Erneut ablegen" schickt genau ihn noch einmal — dieselben
   * Bytes, dieselbe Kennung, derselbe Titel — statt das Blatt aus dem
   * inzwischen vielleicht geänderten Plan neu zu erzeugen. Das Ergebnis steht
   * in `ablage` (für die Vorschau) und beim automatischen Weg zusätzlich im
   * Banner, denn die Vorschau kann inzwischen geschlossen sein.
   *
   * Die Stammdaten (`meta`) gehen NEBEN dem Auftrag mit: bei Freigabe und
   * Versand legt der Kern dazu den Journal-Eintrag des Änderungsdienstes an
   * (Stufe 6.1). Festgehalten wird danach `wiederholung`: derselbe Auftrag
   * mit dem Steckbrief-Anker DIESES Versuchs — „Erneut ablegen" friert so den
   * Stand der Ausgabe ein, nicht den inzwischen geänderten.
   */
  const sendeAblage = async (auftrag: SigePlanAblageAuftrag) => {
    setAblage({ fassungRef: auftrag.fassungRef, stand: { art: 'laeuft' }, auftrag });
    const freigeben = holdIdleExit('SiGe-Plan wird abgelegt');
    try {
      const { stand, hinweise, wiederholung, ausgabeId, vorhandeneAusgabeId } = await legeSigePlanAb(auftrag, meta ?? null);
      setAblage({ fassungRef: auftrag.fassungRef, stand, auftrag: wiederholung });
      setAblageHinweise(hinweise);
      // Jede Ausgabe (Versand oder Freigabe) — der Trigger entscheidet, ob
      // sie einen Bestandteil erledigt; nachgefragt wird mit ihrer Kennung.
      if (auftrag.anlass !== 'entwurf' && stand.art !== 'fehler') void abschluss.nachAblage({ ausgabeId, vorhandeneAusgabeId });
      if (auftrag.anlass === 'versand') {
        // Ton aus dem Stand: ein „schon abgelegt" mit verwaister Datei oder
        // mit einer Entwurfszeile, die nicht umbeschriftet werden konnte
        // (`mitEntwurfsWarnung`), ist eine Warnung mit Hinweistext im Banner,
        // kein uneingeschränkter Erfolg.
        setHinweis({
          ton: BANNER_TON[ablageStandTon(stand)],
          text: ablageStandText(stand),
          aktion: stand.art === 'fehler'
            ? { label: 'Erneut ablegen', onClick: () => { void sendeAblage(wiederholung); } }
            : undefined,
        });
      }
    } finally {
      freigeben();
    }
  };

  /** „Neue Version erstellen" — derselbe Weg wie der Knopf in der Kopfzeile. */
  const oeffneNeueVersion = () => { setVersionLabel(''); setShowVersionDialog(true); };

  /**
   * „Altplan einlesen“ öffnen. Eine noch nicht gesicherte Änderung am
   * Arbeitsschritt geht vorher hinaus — wie vor dem Versionswechsel; scheitert
   * das, bleibt der Dialog zu. Solange er offen ist, lässt sich im Editor
   * nichts ändern.
   */
  const oeffneAltplanImport = async () => {
    if (isReadOnly) return;
    setHinweis(null);
    try {
      await flushSchrittEdits();
    } catch (e) {
      console.error('Ausstehende Änderung am Arbeitsschritt konnte nicht gesichert werden', e);
      setHinweis({
        ton: 'fehler',
        text: `Eine noch nicht gesicherte Änderung am Arbeitsschritt konnte nicht gespeichert werden. Bitte erneut versuchen, bevor ein Altplan eingelesen wird. (${e instanceof Error ? e.message : String(e)})`,
      });
      return;
    }
    setAltplanOffen(true);
  };

  /**
   * Der Altplan steht als neue Version in der Datenbank: dorthin wechseln.
   * Lässt sich eine ausstehende Änderung nicht sichern, wird NICHT gewechselt
   * (das Laden überschriebe sie, wie in handleSelectVersion) — die Version
   * bleibt angelegt und steht in der Versionsliste.
   */
  const nachAltplanImport = async (planId: string, meldung: { ton: 'erfolg' | 'warnung'; text: string }) => {
    // Dialog erst nach dem Sichern schließen: solange er offen ist, kann
    // niemand im Arbeitsschritt dahinter weitertippen.
    try {
      await flushSchrittEdits();
    } catch (e) {
      console.error('Ausstehende Änderung am Arbeitsschritt konnte nicht gesichert werden', e);
      setAltplanOffen(false);
      const { data, error } = await supabase
        .from('sige_projekte').select('*').eq('project_id', project.id).is('deleted_at', null)
        .order('created_at', { ascending: false });
      if (data) setVersions(data as SigeProject[]);
      setHinweis({
        ton: 'fehler',
        text: `${meldung.text}\n\nNicht zur neuen Version gewechselt: eine noch nicht gesicherte Änderung am Arbeitsschritt `
          + `konnte nicht gespeichert werden (${e instanceof Error ? e.message : String(e)}). Die bisherige Version bleibt `
          + (error
            ? 'geöffnet. Die eingelesene Version ist angelegt, die Versionsliste ließ sich aber nicht neu laden. '
              + 'Bitte die Seite neu laden statt den Altplan erneut einzulesen.'
            : 'geöffnet; die eingelesene steht in der Versionsliste.'),
      });
      return;
    }
    setAltplanOffen(false);
    setHinweis(meldung);
    await loadData(planId);
  };

  /**
   * Legt einen Planstand ab. Liegt zu GENAU diesem Stand und Anlass schon ein
   * Auftrag vor (gescheiterter Versuch aus der Vorschau), geht er unverändert
   * noch einmal; sonst entsteht er einmal aus den festgehaltenen Bytes.
   */
  const legeAb = (stand: PlanStandFuerAblage, anlass: SigePlanAblageAnlass) => {
    const frei = sollSigePlanAblegen({ projectId: project.id, isReadOnly });
    if (frei.status === 'fehler') {
      setAblage({ fassungRef: stand.fassungRef, stand: ablageFehler(frei.grund), auftrag: null });
      if (anlass === 'versand') setHinweis({ ton: 'warnung', text: frei.grund });
      return;
    }
    const festgehalten = wiederverwendbarerAuftrag(ablage, stand.fassungRef);
    const vorhanden = festgehalten && festgehalten.anlass === anlass ? festgehalten : null;
    void sendeAblage(vorhanden || baueSigePlanAblageAuftrag({
      projectId: project.id,
      plan: stand.druck,
      datei: stand.blob,
      anlass,
      fassungRef: stand.fassungRef,
      heuteIso: formatDateLocal(new Date()),
      blatt: stand.blatt,
    }));
  };

  /**
   * „Erneut ablegen" aus der Vorschau: wiederholt den FESTGEHALTENEN Auftrag
   * zum angezeigten Stand — dieselben Bytes, dieselbe Kennung, derselbe
   * Anlass. Ein nach dem Export gescheiterter Versand bleibt damit ein
   * Versand, auch wenn der Knopf der Vorschau nach dem Plan-Status „Entwurf"
   * hiesse. Ohne festgehaltenen Auftrag (Riegel griff vor dem Senden) läuft
   * der normale Weg.
   */
  const wiederholeAblage = (stand: PlanStandFuerAblage) => {
    const festgehalten = wiederverwendbarerAuftrag(ablage, stand.fassungRef);
    if (festgehalten) { void sendeAblage(festgehalten); return; }
    legeAb(stand, sigePlanAblageAnlass(sigeProject?.status));
  };

  /**
   * Download — und beim Export MIT Buchung die automatische Ablage. Ein Export
   * ohne Buchung ist ein Zwischenstand und legt nichts ab; dafür gibt es den
   * Knopf in der Vorschau.
   */
  const exportiereUndLegeAb = async (alsFassung: boolean) => {
    if (!sigeProject) return;
    let exportiert: ExportierterPlan;
    setExportLaeuft(true);
    try {
      exportiert = await exportPdf(sigeProject, arbeitsschritte, gefaehrdungen, massnahmen, bloecke, fusszeile);
    } catch (e) {
      setHinweis({ ton: 'fehler', text: `Der PDF-Export ist fehlgeschlagen: ${e instanceof Error ? e.message : String(e)}` });
      return;
    } finally {
      // Im selben Durchlauf wie `legeAb` unten (setzt die Ablage auf „läuft“)
      // — React fasst beides zusammen, dazwischen entsteht keine Lücke.
      setExportLaeuft(false);
    }
    // Als Fassung (Versand) geht der Export hinaus, wenn er gebucht wurde —
    // oder, im Paketprojekt ohne eigene Position, wenn der Anwender ihn
    // ausdrücklich als Fassung ausgibt (Plan B5, Fall 2).
    if (!alsFassung) return;
    // Die Kennung kommt aus dem Export selbst — aus genau den Eingaben, die
    // das Blatt erzeugt haben (Baum, Piktogramme, QR, Fußzeile); hier wird
    // nichts nachgerechnet.
    legeAb({ blob: exportiert.blob, druck: exportiert.druck, fassungRef: exportiert.fassungRef, blatt: exportiert.blatt }, 'versand');
  };

  /**
   * Nach der Abrechnung: der Download exportiert (und legt als Fassung ab),
   * der Plot legt nur ab — sein Blatt ist schon gedruckt, und abgelegt wird
   * genau der geplottete Stand.
   */
  const gibAus = (alsFassung: boolean) => {
    if (abrechnungsZiel.art === 'plot') {
      if (alsFassung) {
        // Danach schließt der Editor (`nachPlotSchliessen`) — eine ältere
        // Meldung soll das nicht aufhalten. Was die Ablage meldet, setzt sie
        // erst danach selbst.
        setHinweis(null);
        abschluss.zuruecksetzen();
        setSchliessenNachPlot(project.id);
        legeAb(abrechnungsZiel.stand, 'versand');
      }
      return;
    }
    void exportiereUndLegeAb(alsFassung);
  };

  /**
   * Fall 2 (Plan B5): Export als Fassung OHNE Positionsbuchung. Die Ablage als
   * versendet ist die Ausgabe, die den Bestandteil „SiGe-Plan" erledigt; ist
   * das Paket damit vollständig, bucht der Abschlussschritt die Paketposition.
   */
  const exportiereAlsPaketFassung = () => {
    setPaketExportFrage(false);
    if (!plotAbrechnungLoslassen()) return;
    if (!sigeProject) return;
    gibAus(true);
  };

  /**
   * Abbrechen bzw. Schließen eines Abrechnungsschritts ohne Ausgabe: Download
   * abgebrochen — oder beim Plot bewusst abgelehnt („Nicht ablegen“,
   * Positionsauswahl geschlossen). Dann wird nichts gebucht und nichts
   * abgelegt; die abgeholte Plot-Abrechnung ist damit erledigt. Schließt alle
   * drei Schritte wie executePdfDownload — offen ist ohnehin nur einer.
   */
  const brichAbrechnungAb = () => {
    setShowBillingModal(false);
    setPaketExportFrage(false);
    setExportOhneAbrechnungConfirm(false);
    plotAbrechnungLoslassen();
  };

  /**
   * Die Bestätigung der Positionsauswahl (Betreiber, Restrisiko 4). Wählt der
   * Anwender in Fall 3 (eigene Position UND Paket) „Vorgang ohne
   * Leistungsbuchung", legte der Export bisher NICHTS ab — keine Ausgabe, der
   * Bestandteil „SiGe-Plan" blieb offen, obwohl der Plan fertig ist. Dann
   * dieselbe Frage wie in Fall 2: als Fassung ausgeben (Versand, erledigt den
   * Bestandteil, die eigene Position wird NICHT gebucht) oder nur
   * herunterladen. Mit gewählter Position bleibt alles wie bisher.
   */
  const nachPositionsauswahl = (serviceIdToBill: string | null, hours: number) => {
    if (!serviceIdToBill
      && schrittOhneLeistungsbuchung({ modul: 'sigeplan', pakete: abschluss.pakete, sperre: abschluss.sperre }) === 'fassung_fragen') {
      // Der Schritt geht in der Paket-Frage weiter — eine festgehaltene
      // Plot-Abrechnung bleibt festgehalten, erst deren Antwort lässt sie los.
      setShowBillingModal(false);
      setPaketExportFrage(true);
      return;
    }
    executePdfDownload(serviceIdToBill, hours);
  };

  const executePdfDownload = (serviceIdToBill: string | null, hours: number) => {
    setShowBillingModal(false);
    setExportOhneAbrechnungConfirm(false);
    setPaketExportFrage(false);
    // Beim Plot: loslassen vor der Buchung; anderes Projekt → zurückgelegt.
    if (!plotAbrechnungLoslassen()) return;
    if (!sigeProject) return;

    // Der Stand, der JETZT gilt — ueber den gemeinsamen Ref-Schreibweg des
    // Moduls (Codex R2c): ein Abschlussschritt einer frueheren Ausgabe kann
    // inzwischen ein Paket gebucht haben (BST-…); der Stand aus dem Render
    // setzte diese Buchung sonst zurueck. Geschrieben wird nur in das Projekt,
    // das beim Klick offen war.
    const stand = abschluss.holeBericht() ?? project.data;
    const standMeta = stand.metadata;
    const projektDesExports = projektDesVorgangs(stand);

    // Gebucht ist, was wirklich geschrieben wurde — „abgelegt beim Export mit
    // Buchung" behauptet so nichts, was nicht gebucht wurde.
    let gebucht = false;

    if (serviceIdToBill && onUpdate) {
      const services = standMeta?.globalData?.services || [];
      const serviceIndex = services.findIndex(s => s.id === serviceIdToBill);
      // Position verschwunden: keine Buchung, und das wird gesagt (Codex R3).
      if (serviceIndex === -1) setHinweis({ ton: 'warnung', text: POSITION_FEHLT_TEXT });
      if (serviceIndex !== -1) {
        const newServices = [...services];
        const s = newServices[serviceIndex];
        const increment = isHourlyUnit(s.unit) ? hours : 1;
        const newUsed = (s.quantityUsed || 0) + increment;
        const isDone = s.quantityContracted > 0 && newUsed >= s.quantityContracted;

        newServices[serviceIndex] = {
          ...s,
          quantityUsed: newUsed,
          status: isDone ? 'done' : s.status
        };

        // Beim Plot nennt die Buchung den geplotteten Plan — die Anzeige kann
        // inzwischen auf einer anderen Version stehen.
        const gebuchterPlan = abrechnungsZiel.art === 'plot' ? abrechnungsZiel.stand.druck : sigeProject;
        const logEntry = {
          id: crypto.randomUUID(),
          date: new Date().toISOString(),
          serviceId: s.id,
          quantity: increment,
          performerId: 'system',
          performerName: standMeta?.coordinator || 'System',
          isSubstitution: false,
          description: `${abrechnungsBuchungsText(abrechnungsZiel.art, gebuchterPlan.plan_nr, gebuchterPlan.status)} (${increment} ${s.unit})`
        };

        const previousLog = standMeta.globalData?.serviceLog || [];
        const verworfen = abschluss.schreibeFuer(projektDesExports, {
          ...stand,
          metadata: {
            ...standMeta,
            globalData: {
              ...(standMeta.globalData || { status: 'active' as any }),
              services: newServices,
              serviceLog: [...previousLog, logEntry],
            }
          }
        });
        if (verworfen) {
          // Nicht geschrieben → nichts vormerken, nicht als Versand ablegen.
          setHinweis({ ton: 'warnung', text: verworfenText(verworfen, 'position') });
          gibAus(false);
          return;
        }
        gebucht = true;

        // Zweitschrift ins Buchungsjournal — nur VORGEMERKT: dieser Pfad
        // speichert nicht selbst, sondern reicht ueber onUpdate an die
        // Offline-Queue weiter; geschrieben wird zentral nach dem bestaetigten
        // Flush. Die Vormerkung steht INNERHALB des Zweigs, in dem wirklich
        // gebucht wurde — ein Export ohne Abrechnung darf nichts vormerken.
        // Projekt-id ist `project.id`: dieselbe, mit der der Editor seine
        // Plandaten liest (`.eq('project_id', project.id)`), also die echte
        // Zeile in `projects`.
        const projektId = (project.id || '').trim();
        if (projektId && projektId !== 'temp-id') {
          queueJournalMirror({
            projectId: projektId,
            entries: [logEntry],
            previousLog,
            source: 'einweisung',
            linkedProjectConfig: standMeta.globalData?.linkedProjectConfig ?? null,
            // Sicher gewusst: ein Plan-Export ist weder Zusatzleistung noch
            // der Nachweis einer BEGEHUNG.
            statements: { [logEntry.id]: { isAddon: false, coversInspection: false } },
          });
        } else {
          console.warn('[SiGePlanEditor] Buchung ohne belastbare Projekt-id, nicht im Journal vorgemerkt.');
        }
      }
    }
    // Download (beim Plot nicht) — und mit Buchung die Ablage als Versand.
    gibAus(gebucht);
  };

  // Zugangssperre statt Riegel pro Schreibaufruf: der Editor und seine
  // Unterkomponenten (Arbeitsschritte, Blöcke, Detail, Upload, Assistent)
  // schreiben an rund 25 Stellen direkt nach Supabase — an
  // App.handleUpdateReport und damit am Lesemodus vorbei. Eine Änderung waere
  // hier also nicht nur "verpufft", sondern dauerhaft. Bis jede dieser Stellen
  // einen eigenen Riegel hat, bleibt das Modul im Lesemodus zu.
  if (isReadOnly) {
    return (
      <div data-glass-view="module-sige-plan-editor" data-glass-bereich="module" className="flex flex-col h-full bg-slate-50">
        <div data-glass="kopf" className="bg-white border-b border-slate-200 p-4 flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h1 className="text-xl font-bold text-slate-800">SiGe-Plan</h1>
        </div>
        {/* Lesend: offene Abweichungen zur Planungsphase darf auch sehen, wer
            nicht ändern darf — nur ohne den Weg „Neue Fassung". Der Rahmen
            verschwindet (`empty:hidden`), wenn der Hinweis nichts zeigt. */}
        <div className="px-4 pt-3 shrink-0 empty:hidden">
          <AbweichungsHinweis
            projectId={project.id}
            metadata={meta}
            unterlage="sigeplan"
            isReadOnly
          />
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-lg bg-amber-50 border border-amber-300 rounded-xl p-5 text-sm text-amber-900">
            <p className="font-bold mb-2">Nur-Ansicht: SiGe-Plan gesperrt</p>
            <p>
              Dieses Projekt ist schreibgeschützt geöffnet. Der SiGe-Plan-Editor speichert
              unmittelbar in die Datenbank. In der Nur-Ansicht ließe sich der Plan sonst
              dauerhaft verändern, obwohl nichts gespeichert werden soll. Er ist deshalb
              hier nicht verfügbar.
            </p>
            <p className="mt-2">Zum Bearbeiten das Projekt über die Projektliste öffnen.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="flex-1 flex justify-center items-center h-full"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;
  }

  return (
    <div data-glass-view="module-sige-plan-editor" data-glass-bereich="module" className="flex flex-col h-full bg-slate-50 relative">
      <div data-glass="kopf" className="bg-white border-b border-slate-200 p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-800">SiGe-Plan</h1>
            <p className="text-xs text-slate-500">
              {sigeProject?.baumassnahme_name || project.name}
              {sigeProject?.status && ` (${sigeProject.status})`}
            </p>
          </div>

          {/* Versionen: jede Auswahl lädt einen eigenen Zeilenbaum */}
          <div className="flex items-center gap-2 pl-4 border-l border-slate-200">
            <History className="w-4 h-4 text-slate-400 shrink-0" />
            <select
              value={sigeProject?.id || ''}
              onChange={(e) => handleSelectVersion(e.target.value)}
              disabled={isDuplicating || versions.length === 0}
              title="Versionen"
              className="max-w-[16rem] text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:opacity-50"
            >
              {versions.length === 0 && <option value="">Keine Version</option>}
              {versions.map((v, idx) => (
                <option key={v.id} value={v.id}>
                  {describeSigeVersion(v)}{idx === 0 ? ' (neueste)' : ''}
                </option>
              ))}
            </select>
            <button
              data-tutorial="sigeplan.version-neu"
              onClick={oeffneNeueVersion}
              disabled={!sigeProject || isDuplicating}
              className="px-3 py-1.5 text-xs font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              {isDuplicating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
              Neue Version erstellen
            </button>
            <button
              onClick={() => { void oeffneAltplanImport(); }}
              disabled={isDuplicating}
              title="SiGe-Plan aus der Zeit vor der App (PDF) als Version einlesen"
              className="px-3 py-1.5 text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              <FileUp className="w-3.5 h-3.5" />
              Altplan einlesen
            </button>
          </div>
        </div>

        <PlanToolbar
          onOpenWizard={() => setWizardOffen(true)}
          onOpenStempelfeld={mitPlan(() => setIsStempelfeldOpen(true))}
          onOpenAnalysis={mitPlan(() => setIsUploadOpen(true))}
          onOpenDokumente={() => setDokumenteOffen(true)}
          onExportPDF={mitPlan(() => setIsVorschauOpen(true))}
          onDruckNachtragen={sigeProject ? () => setDruckNachtragenOffen(true) : undefined}
        />
      </div>

      {hinweis && (
        <div
          className={`px-4 py-2.5 border-b text-xs flex justify-between items-start gap-3 shrink-0 ${
            hinweis.ton === 'fehler'
              ? 'bg-red-50 border-red-200 text-red-700'
              : hinweis.ton === 'erfolg'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : 'bg-amber-50 border-amber-200 text-amber-800'
          }`}
        >
          <span className="whitespace-pre-wrap flex-1">{hinweis.text}</span>
          {hinweis.aktion && (
            <button
              type="button"
              onClick={hinweis.aktion.onClick}
              className="px-2 py-0.5 shrink-0 font-bold underline"
            >
              {hinweis.aktion.label}
            </button>
          )}
          <button onClick={() => setHinweis(null)} className="px-1 shrink-0 opacity-60 hover:opacity-100" title="Meldung schließen">✕</button>
        </div>
      )}

      {/* Stunden-Pflichtfenster (Stundenkontingent S3) — genau einmal je Modul. */}
      <BestandteilStundenPflicht pflicht={abschluss.stundenPflicht} />

      {/* Plan B5, Fall 2/3: was die Ausgabe an Bestandteilen erledigt hat. */}
      {abschluss.stand && (
        <div className="px-4 py-2 border-b border-slate-200 shrink-0">
          <BestandteilAbschlussHinweis stand={abschluss.stand} onSchliessen={abschluss.zuruecksetzen} />
        </div>
      )}

      {/* Anmerkungen des Ablage-Kerns — dezent, nie im Fehlerton: die Fassung liegt. */}
      {ablageHinweise.length > 0 && (
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 text-[11px] text-slate-500 flex justify-between items-start gap-3 shrink-0">
          <span className="flex-1">{ablageHinweise.join(' · ')}</span>
          <button onClick={() => setAblageHinweise([])} className="px-1 shrink-0 opacity-60 hover:opacity-100" title="Hinweis schließen">✕</button>
        </div>
      )}

      {/* Änderungsdienst (Stufe 6.1): weicht die Planungsphase vom Stand der
          letzten Ausgabe ab, steht es hier — mit dem vorhandenen Weg „Neue
          Version erstellen" als neue Fassung. Der Rahmen gibt dem Hinweis die
          Seitenränder, die dieses Layout sonst nicht hat, und verschwindet
          (`empty:hidden`), wenn nichts zu zeigen ist. */}
      <div className="px-4 pt-3 shrink-0 empty:hidden">
        <AbweichungsHinweis
          projectId={project.id}
          metadata={meta}
          unterlage="sigeplan"
          isReadOnly={isReadOnly}
          onNeueFassung={sigeProject ? oeffneNeueVersion : undefined}
        />
      </div>

      {!sigeProject && !initError && (
        <div className="bg-violet-50 border-b border-violet-200 px-4 py-3 shrink-0 flex flex-wrap items-center justify-between gap-3">
          <div className="text-xs text-violet-900">
            <strong>Für dieses Projekt existiert noch kein SiGe-Plan.</strong> Der Assistent legt ihn
            in sechs Schritten an: Plankopf, Arbeitsschritte, Maßnahmen, Blöcke, Prüfung. Gibt es
            schon einen Plan aus der Zeit vor der App, lässt er sich als PDF einlesen.
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => { void oeffneAltplanImport(); }}
              className="px-3 py-1.5 text-xs font-bold text-violet-700 bg-white border border-violet-200 hover:bg-violet-100 rounded-lg transition-colors flex items-center gap-1.5"
            >
              <FileUp className="w-3.5 h-3.5" /> Altplan einlesen
            </button>
            <button
              onClick={() => setWizardOffen(true)}
              className="px-3 py-1.5 text-xs font-bold text-white bg-violet-600 hover:bg-violet-700 rounded-lg shadow transition-colors flex items-center gap-1.5"
            >
              <Wand2 className="w-3.5 h-3.5" /> Assistent starten
            </button>
          </div>
        </div>
      )}

      {initError && (
        <div className="bg-red-50 border-b border-red-200 p-4 shrink-0">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-sm font-bold text-red-800">Fehler beim Initialisieren des SiGe-Plans</h3>
              <p className="text-xs text-red-700 mt-1 whitespace-pre-wrap font-mono bg-white/50 p-2 rounded border border-red-100">{initError}</p>
              <p className="text-xs text-slate-500 mt-2">
                <strong>Diagnose & Lösung:</strong> Falls dieser Fehler auftritt, existiert vermutlich noch kein zugewiesener Plan-Eintrag in der Datenbank für dieses Projekt. Generieren Sie ihn per Klick auf "Projekt-Eintrag erzwingen" oder laden Sie die Seite neu.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => loadData()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-semibold shadow transition-colors shrink-0"
              >
                Erneut versuchen
              </button>
              <button
                onClick={async () => {
                  // Laufsperre: ein zweiter Klick vor dem nächsten Rendern legte
                  // sonst einen zweiten Plan an (die Anlage wartet ggf. auf die
                  // Plotter-Vorlagen).
                  if (erzwingenLaeuftRef.current) return;
                  erzwingenLaeuftRef.current = true;
                  setLoading(true);
                  try {
                    // Neuer Plan: Standardbreite der Firma (Plotter-Vorlagen),
                    // nach dem Laden der Vorlagen — sie wird dauerhaft gespeichert.
                    const anlage = await sigePlanVorlagenFuerAnlage();
                    // Sitzung gewechselt, während gewartet wurde: still abbrechen —
                    // die Ansicht des alten Nutzers ist ohnehin weg.
                    if (anlage.abgebrochen === true) return;
                    const { error } = await supabase.from('sige_projekte').insert({
                      project_id: project.id,
                      baumassnahme_name: project.name || 'SiGe-Projekt (Erzwungen)',
                      plan_inhalt: 'Sicherheits- und Gesundheitsschutzplan',
                      ...vorbelegungNeuerPlan(anlage.vorlagen),
                    });
                    if (error) setInitError('Eintrag erzwingen fehlgeschlagen: ' + error.message);
                    else loadData();
                  } catch (err: any) {
                    setInitError('Eintrag erzwingen fehlgeschlagen: ' + (err?.message || String(err)));
                  } finally {
                    setLoading(false);
                    erzwingenLaeuftRef.current = false;
                  }
                }}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold shadow transition-colors shrink-0"
              >
                Projekt-Eintrag erzwingen
              </button>
            </div>
          </div>
        </div>
      )}

      <div data-glass="spalten" className="flex-1 flex overflow-hidden">
        {/* Left Column: Arbeitsschritte */}
        <div className="w-80 bg-white border-r border-slate-200 flex flex-col overflow-hidden">
          <ArbeitsschritteListe
            sigeProjektId={sigeProject?.id}
            schritte={arbeitsschritte}
            selectedSchrittId={selectedSchrittId}
            beteiligte={beteiligte}
            projektVon={sigeProject?.zeitraum_von}
            projektBis={sigeProject?.zeitraum_bis}
            hauptabschnitte={hauptabschnitte}
            katalog={katalog}
            onAbschnittCreated={handleAbschnittCreated}
            onSelectSchritt={setSelectedSchrittId}
            onSchritteChange={(neueSchritte) => setArbeitsschritte(neueSchritte)}
            isReadOnly={isReadOnly}
            ohneZeitachse={!planHatZeitachse(sigeProject?.darstellung)}
            gefaehrdungen={gefaehrdungen}
            massnahmen={massnahmen}
            onFlush={flushSchrittEdits}
            onAbschnittLeer={handleAbschnittLeer}
            onGefaehrdungenUmgehaengt={handleGefaehrdungenUmgehaengt}
          />
        </div>

        {/* Center Column: Matrix */}
        <div data-glass="seite" className="flex-1 bg-slate-50 overflow-hidden flex flex-col">
          {selectedSchrittId && arbeitsschritte.find(s => s.id === selectedSchrittId) ? (
            <SchrittDetail
              schritt={arbeitsschritte.find(s => s.id === selectedSchrittId)!}
              onSchrittChange={(s) => setArbeitsschritte(prev => prev.map(a => a.id === s.id ? s : a))}
              onRegisterFlush={registerSchrittFlush}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-400">
              Bitte wählen Sie einen Arbeitsschritt aus der Liste.
            </div>
          )}
        </div>

        {/* Right Column: Block Manager */}
        <div className="w-80 bg-white border-l border-slate-200 flex flex-col overflow-hidden">
          <BlockManager
            sigeProjektId={sigeProject?.id}
            bloecke={bloecke}
            beteiligte={beteiligte}
            adresse={sigeProject?.baumassnahme_adresse}
            onBloeckeChange={setBloecke}
          />
        </div>
      </div>

      {isStempelfeldOpen && sigeProject && (
        <StempelfeldEditor
          project={sigeProject}
          userProfile={userProfile}
          reportMeta={meta}
          onClose={() => setIsStempelfeldOpen(false)}
          onSave={updateSigeProject}
        />
      )}

      {dokumenteOffen && (
        <DokumentePanel
          projectId={project.id}
          modul="sigeplan"
          userProfile={userProfile}
          isReadOnly={isReadOnly}
          onSchliessen={() => setDokumenteOffen(false)}
        />
      )}

      {isUploadOpen && sigeProject && (
        <DokumentenUpload
          sigeProjektId={sigeProject.id}
          projektVon={sigeProject.zeitraum_von}
          projektBis={sigeProject.zeitraum_bis}
          ohneZeitachse={!planHatZeitachse(sigeProject.darstellung)}
          startSortierung={naechsteSortierung(arbeitsschritte)}
          hauptabschnitte={hauptabschnitte}
          vorhandeneBezeichnungen={arbeitsschritte.map(s => s.bezeichnung)}
          onClose={() => setIsUploadOpen(false)}
          onResultsApplied={() => loadData()}
        />
      )}

      {/* Knopf „Fassung/Entwurf in Dokumente ablegen" in der Vorschau: der
          Anlass folgt dem Plan-Status (nur `geprueft` ist eine Fassung). */}
      {isVorschauOpen && sigeProject && (
        <PlanVorschau
          sigeProject={sigeProject}
          arbeitsschritte={arbeitsschritte}
          gefaehrdungen={gefaehrdungen}
          massnahmen={massnahmen}
          bloecke={bloecke}
          fusszeile={fusszeile}
          onDownload={handleDownloadRequest}
          onSchriftAendern={isReadOnly ? undefined : pt => updateSigeProject({ darstellung: mitPlanSchrift(sigeProject.darstellung, pt, sigePlanVorlagen.standardSchriftPt) })}
          onAblegen={stand => legeAb(stand, sigePlanAblageAnlass(sigeProject.status))}
          onErneutAblegen={wiederholeAblage}
          plotAbrechnungProjektId={project.id}
          ablage={ablage}
          onClose={() => setIsVorschauOpen(false)}
        />
      )}

      {druckNachtragenOffen && sigeProject && (
        <DruckNachtragenDialog
          // Projektwechsel ohne Schließen: verbindlich neu aufbauen (Formular, Listen, Absicht).
          key={project.id}
          projectId={project.id}
          sigeProject={sigeProject}
          fusszeile={fusszeile}
          eigeneNutzerId={userProfile?.id ?? null}
          istAdmin={userProfile?.role === 'admin' || userProfile?.role === 'super_admin'}
          onClose={() => setDruckNachtragenOffen(false)}
        />
      )}

      {altplanOffen && !isReadOnly && (
        <AltplanImportDialog
          projectId={project.id}
          nutzer={userProfile}
          vorhandeneVersionen={versions.length}
          onClose={() => setAltplanOffen(false)}
          onAngelegt={(planId, meldung) => { void nachAltplanImport(planId, meldung); }}
        />
      )}

      {showVersionDialog && (
        <div className="fixed inset-0 z-[150] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-slate-900 text-white p-5 flex items-center gap-3">
              <Save className="w-6 h-6 text-blue-400" />
              <h3 className="text-lg font-bold">Neue Version erstellen</h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600">
                Die aktuelle Version wird vollständig kopiert: Arbeitsschritte, Gefährdungen, Schutzmaßnahmen und Blöcke. Die bisherige Version bleibt unverändert erhalten und weiterhin auswählbar.
              </p>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Bezeichnung</label>
                <input
                  autoFocus
                  type="text"
                  value={versionLabel}
                  onChange={(e) => setVersionLabel(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreateVersion(); }}
                  placeholder="z. B. Fortschreibung Rohbau"
                  className="w-full p-2.5 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                />
                <p className="text-xs text-slate-400 mt-1">Ohne Eingabe wird fortlaufend nummeriert.</p>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => setShowVersionDialog(false)} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">Abbrechen</button>
                <button onClick={handleCreateVersion} className="px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow transition-colors">Version erstellen</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rückfrage vor dem Export ohne Abrechnungsbezug — liegt über der
          Plan-Vorschau, aus der sie ausgelöst wird. */}
      {exportOhneAbrechnungConfirm && (
        <div className="fixed inset-0 z-[160] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="p-5 space-y-3">
              <h3 className="text-base font-bold text-slate-800">PDF ohne Abrechnung exportieren?</h3>
              <p className="text-sm text-slate-600">
                Für dieses Projekt sind keine Abrechnungs-Leistungen hinterlegt. Der Export wird deshalb keiner Leistung zugebucht.
              </p>
            </div>
            <div className="px-5 pb-5 flex justify-end gap-3">
              <button
                onClick={brichAbrechnungAb}
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={() => executePdfDownload(null, 1)}
                className="px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow transition-colors"
              >
                Trotzdem exportieren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan B5, Fall 2: keine eigene SiGe-Plan-Position, aber ein Paket mit dem
          Bestandteil „SiGe-Plan". Statt der Positionsauswahl die Frage, ob der
          Export als Fassung hinausgeht — nur dann erledigt er den Bestandteil.
          Dieselbe Frage in Fall 3 nach „Vorgang ohne Leistungsbuchung"
          (nachPositionsauswahl, Restrisiko 4). */}
      {paketExportFrage && (
        <div className="fixed inset-0 z-[160] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="p-5 space-y-3">
              <h3 className="text-base font-bold text-slate-800">SiGe-Plan als Fassung ausgeben?</h3>
              <p className="text-sm text-slate-600">
                Der SiGe-Plan ist Bestandteil der Position
                {' '}{abschluss.pakete.map(p => `„${p.title}“`).join(', ')}.
                Als Fassung wird er als versendet in „Dokumente“ abgelegt und erledigt diesen Bestandteil.
                Eine eigene Position wird nicht gebucht.
              </p>
              <p className="text-xs text-slate-500">
                {abrechnungsZiel.art === 'plot'
                  ? '„Nicht ablegen“: der Plot bleibt ein Zwischenstand, der Bestandteil bleibt offen.'
                  : '„Nur herunterladen“ ist ein Zwischenstand: nichts wird abgelegt, der Bestandteil bleibt offen.'}
              </p>
            </div>
            <div className="px-5 pb-5 flex flex-wrap justify-end gap-3">
              <button
                onClick={brichAbrechnungAb}
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                {abrechnungsZiel.art === 'plot' ? 'Nicht ablegen' : 'Abbrechen'}
              </button>
              {/* Beim Plot gibt es nichts herunterzuladen — „Nicht ablegen" ist der Zwischenstand. */}
              {abrechnungsZiel.art !== 'plot' && (
                <button
                  onClick={() => executePdfDownload(null, 1)}
                  className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  Nur herunterladen
                </button>
              )}
              <button
                onClick={exportiereAlsPaketFassung}
                className="px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow transition-colors"
              >
                Als Fassung ausgeben
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export-Gate: benennt die harten Lücken, lässt den Export aber zu. */}
      {exportPruefung && (
        <div className="fixed inset-0 z-[160] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="p-5 space-y-3">
              <h3 className="text-base font-bold text-slate-800">Der Plan ist noch unvollständig</h3>
              <p className="text-sm text-slate-600">
                Vollständigkeit: <strong>{exportPruefung.score} von 100</strong>. Folgende Punkte fehlen:
              </p>
              <ul className="space-y-1.5">
                {exportPruefung.fehlend.filter(f => !f.hinweis).map(f => (
                  <li
                    key={f.id}
                    className={`text-xs border rounded-lg px-2.5 py-2 ${
                      f.blockierend ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'
                    }`}
                  >
                    {f.text}
                  </li>
                ))}
              </ul>
            </div>
            <div className="px-5 pb-5 flex flex-wrap justify-end gap-3">
              <button
                onClick={() => setExportPruefung(null)}
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={() => { setExportPruefung(null); setIsVorschauOpen(false); setWizardOffen(true); }}
                className="px-4 py-2 text-sm font-bold text-violet-700 bg-violet-50 hover:bg-violet-100 rounded-lg transition-colors"
              >
                Im Assistenten ergänzen
              </button>
              <button
                onClick={() => { setExportPruefung(null); starteExportFlow(); }}
                className="px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow transition-colors"
              >
                Trotzdem exportieren
              </button>
            </div>
          </div>
        </div>
      )}

      {wizardOffen && (
        <SiGePlanWizard
          projectId={project.id}
          projectName={project.name}
          reportMeta={meta}
          beteiligte={beteiligte}
          sigeProject={sigeProject}
          arbeitsschritte={arbeitsschritte}
          bloecke={bloecke}
          gefaehrdungen={gefaehrdungen}
          massnahmen={massnahmen}
          hauptabschnitte={hauptabschnitte}
          onClose={(geaendert) => {
            setWizardOffen(false);
            if (geaendert) loadData();
          }}
          onAbgeschlossen={() => setIsVorschauOpen(true)}
        />
      )}

      {showBillingModal && (
        <BillingSelectionModal
          isOpen={showBillingModal}
          onClose={brichAbrechnungAb}
          onConfirm={nachPositionsauswahl}
          services={billingServices}
          title={abrechnungsZiel.art === 'plot' ? 'Geplotteten SiGe-Plan abrechnen' : 'SiGe-Plan abrechnen'}
          context="sigeplan"
          memoryScope={project.id}
          canSeeFinancials={rechte.hat('preise.sehen')}
          vorabHinweis={schonGebuchtHinweis(eigenePositionen)}
        />
      )}
    </div>
  );
};
